/**
 * CE QUE LES DEUX PDF DU PLANNING ANNUEL ONT EN COMMUN (lot 5.7).
 *
 * POURQUOI CE FICHIER EXISTE. L'export Calendrier (lot 5.6) portait dans son
 * propre fichier les deux logos, l'en-tête et le pied de page. L'export
 * Événements a besoin des trois à l'identique — c'est la demande de Bertrand du
 * 2026-08-12 : « dans le même esprit, les deux logos ». Les recopier aurait créé
 * deux en-têtes pour une seule identité visuelle, donc deux occasions de
 * diverger : le jour où l'ordre des logos change, ou leur taille, l'un suivrait
 * et l'autre non. C'est exactement la faute que `planningPeriodes.js` a corrigée
 * au lot 5.6 pour le calcul des cellules.
 *
 * ⚠️ CONTRAINTE DE CETTE EXTRACTION : le PDF du calendrier ne doit pas bouger
 * d'un millimètre. Les valeurs par défaut ci-dessous sont donc EXACTEMENT celles
 * qu'il employait en dur (bande de logos à y = 2 sur 12 mm, bornée à 32 mm de
 * large ; titre à y = 9 en corps 15 ; sous-titre à y = 14 en corps 8 ; pied en
 * corps 6). Vérifié en comparant le fichier produit avant et après.
 */

/**
 * Charge une image (logo de département, logo d'université, image d'un événement)
 * sous la forme que jsPDF sait poser : { data, width, height }.
 *
 * Résout à `null` plutôt que de rejeter : une identité visuelle absente ou
 * illisible ne doit pas emporter l'export. Le PDF sort sans logo, pas en erreur.
 *
 * ⚠️ Un SVG sans dimensions intrinsèques se charge avec une taille NULLE : le
 * dessiner produirait une image vide, et diviser par sa hauteur pour garder ses
 * proportions donnerait l'infini. Le cas se produit vraiment — les réglages
 * généraux acceptent le SVG pour le logo de l'université.
 */
/**
 * `hauteurMax` (Planning oraux, PO.5b, 2026-10-16) : l'image est réduite à cette hauteur en pixels — un logo imprimé sur 12 mm
 * n'a pas besoin de plus de ~160 px ; mesuré : le logo de QLIO passe de 182 Ko à 42 Ko dans CHAQUE convocation jointe.
 * Sans le paramètre, rien ne change (Planning annuel).
 */
export function chargerImagePourPdf(url, hauteurMax = null) {
    return new Promise((resolve) => {
        if (!url) { resolve(null); return; }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (!img.naturalWidth || !img.naturalHeight) { resolve(null); return; }
            const k = hauteurMax && img.naturalHeight > hauteurMax ? hauteurMax / img.naturalHeight : 1;
            const c = document.createElement('canvas');
            c.width = Math.max(1, Math.round(img.naturalWidth * k));
            c.height = Math.max(1, Math.round(img.naturalHeight * k));
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            resolve({ data: c.toDataURL('image/png'), width: c.width, height: c.height });
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

/** '#RRGGBB' -> {r,g,b}. Une couleur illisible devient noire plutôt qu'absente. */
export function versRgb(hex) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    return m
        ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
        : { r: 0, g: 0, b: 0 };
}

/** 'AAAA-MM-JJ' -> '15/09' — la forme courte, quand la place manque. */
export function dateCourte(iso) {
    const [, m, j] = String(iso).split('-');
    return j + '/' + m;
}

/** 'AAAA-MM-JJ' -> '15/09/2025' — la forme de la maquette de l'export Événements. */
export function dateComplete(iso) {
    const [a, m, j] = String(iso).split('-');
    return j + '/' + m + '/' + a;
}

/** Les douze mois en clair, pour les bandeaux de regroupement. */
export const MOIS_FR = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
];

/** 'AAAA-MM' -> 'Septembre 2025'. */
export function libelleMois(cleMois) {
    const [annee, mois] = String(cleMois).split('-');
    const nom = MOIS_FR[parseInt(mois, 10) - 1] || cleMois;
    return nom.charAt(0).toUpperCase() + nom.slice(1) + ' ' + annee;
}

/**
 * Pose un logo dans un cadre, en respectant ses proportions, et renvoie la
 * largeur réellement occupée (0 s'il n'y avait rien à poser).
 *
 * Les deux logos de l'application — celui de l'UNIVERSITÉ (réglages généraux,
 * `logo_universite`) et celui du DÉPARTEMENT (`departements.logo_url`) — n'ont
 * ni la même forme ni le même rapport largeur/hauteur : un bandeau très large
 * d'un côté, un écusson presque carré de l'autre. Les caler sur une hauteur
 * commune et laisser la largeur suivre est la seule façon de ne déformer ni l'un
 * ni l'autre.
 */
export function dessinerLogo(pdf, logo, x, hautBande, hauteurBande, lMax) {
    if (!logo?.data || !logo.width || !logo.height) return 0;
    const ratio = logo.width / logo.height;
    let h = hauteurBande;
    let l = h * ratio;
    if (l > lMax) { l = lMax; h = l / ratio; }
    try {
        // CENTRÉ dans la hauteur de la bande, et non calé en haut : un bandeau
        // très large ne fait que 6 mm de haut quand l'écusson en fait 12, et les
        // aligner par le haut donne deux logos qui flottent à des hauteurs
        // différentes. Centrés, ils se lisent comme une seule ligne d'en-tête.
        pdf.addImage(logo.data, 'PNG', x, hautBande + (hauteurBande - h) / 2, l, h);
        return l;
    } catch {
        return 0;   // un logo illisible ne doit pas empêcher l'export
    }
}

/**
 * L'EN-TÊTE COMMUN : les deux logos à gauche, le titre et le sous-titre centrés.
 *
 * L'université d'abord, le département ensuite — demande de Bertrand le
 * 2026-08-09. C'est l'ordre de la tutelle vers l'entité, celui qu'on trouve sur
 * les documents officiels, et celui de la page de connexion où le logo de
 * l'université ouvre l'application.
 *
 * Le logo de l'université se lit sur `/api/branding` et non sur
 * `/api/admin/settings` : les deux le renvoient, mais la seconde exige d'être
 * super-administrateur — un chef de département aurait reçu 403 et son PDF
 * serait sorti sans logo, sans que rien ne l'explique.
 *
 * @returns {number} l'ordonnée du bas de l'en-tête, d'où le contenu peut partir.
 */
export function dessinerEnTete(pdf, {
    largeurPage,
    margeX = 5,
    logo = null,
    logoUniversite = null,
    titre = '',
    titreParDefaut = '',
    tailleTitre = 15,
    yTitre = 9,
    sousTitre = '',
    tailleSousTitre = 8,
    ySousTitre = 14,
    hautBande = 2,
    hauteurBande = 12,
    largeurLogoMax = 32,
    ecartLogos = 3
}) {
    const largeurUniv = dessinerLogo(pdf, logoUniversite, margeX, hautBande, hauteurBande, largeurLogoMax);
    dessinerLogo(
        pdf, logo,
        margeX + (largeurUniv ? largeurUniv + ecartLogos : 0),
        hautBande, hauteurBande, largeurLogoMax
    );

    pdf.setFontSize(tailleTitre);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(15, 23, 42);
    pdf.text(titre || titreParDefaut, largeurPage / 2, yTitre, { align: 'center' });

    if (sousTitre) {
        pdf.setFontSize(tailleSousTitre);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(sousTitre, largeurPage / 2, ySousTitre, { align: 'center' });
    }

    return Math.max(hautBande + hauteurBande, sousTitre ? ySousTitre : yTitre);
}

/**
 * LE PIED COMMUN : « Généré le … par … » à gauche, « p. X/Y » à droite.
 *
 * Le nombre total de pages est un PARAMÈTRE et non un compteur interne : les
 * deux générateurs calculent leur pagination AVANT de dessiner, précisément pour
 * que « p. 1/3 » soit juste dès la première page. Un pied qui s'écrirait au fil
 * de l'eau devrait attendre la fin pour connaître le total, et jsPDF ne permet
 * pas de revenir écrire sur une page déjà quittée sans la rouvrir.
 */
export function dessinerPied(pdf, {
    largeurPage,
    y,
    margeX = 5,
    page,
    pages,
    auteur = '',
    taille = 6
}) {
    pdf.setFontSize(taille);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(148, 163, 184);
    pdf.text(
        'Généré le ' + new Date().toLocaleDateString('fr-FR') + (auteur ? ' par ' + auteur : ''),
        margeX, y
    );
    pdf.text('p. ' + page + '/' + pages, largeurPage - margeX, y, { align: 'right' });
}
